// ✅ ContractorEntry.jsx (FULL FILE — UPDATED WITH commonHeaders)
// ✅ Added:
// 1) localStorage values:
//    - finYearId
//    - finYear
//    - branchId
//    - branchType
//    - userId
// 2) commonHeaders via useMemo
// 3) Passed headers in all axios API calls
// 4) Payload still sends:
//    - branchid = logged-in branchId
//    - branchId = selected branch dropdown value
//    - userid = logged-in userId

import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft } from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

const ContractorEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  const mode = location.state?.mode || "add";
  const passedContractor = location.state?.contractor || null;

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdLS = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userIdLS = localStorage.getItem("userId") || "";

  // ✅ parsed values
  const sessionBranchId = useMemo(() => {
    const n = parseInt(branchIdLS, 10);
    return Number.isFinite(n) ? n : null;
  }, [branchIdLS]);

  const userid = useMemo(() => {
    const n = parseInt(userIdLS, 10);
    return Number.isFinite(n) ? n : null;
  }, [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branchType,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchType, userIdLS]
  );

  // ✅ helper: first non-empty
  const pick = (...vals) => {
    for (const v of vals) {
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
    }
    return "";
  };

  // Dropdowns
  const [branches, setBranches] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [loadingDDL, setLoadingDDL] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);

  // ✅ Form
  const [formData, setFormData] = useState({
    contractorMasId: null,
    contractorId: "",
    contractorName: "",
    mobileNumber: "",
    branchId: "",

    countryId: "56",
    stateId: "24",
    districtId: "",

    landMark: "",
    address1: "",
    address2: "",

    accNo: "",
    ifscCode: "",
    bankName: "",
    branchNameBank: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Extract numeric code from "Branch 8/12"
  const extractCodeNumeric = (contractorIdStr) => {
    const s = String(contractorIdStr || "").trim();
    if (!s.includes("/")) return null;
    const last = s.split("/").pop();
    const n = parseInt(last, 10);
    return Number.isFinite(n) ? n : null;
  };

  // ✅ Prefill from listing (EDIT)
  useEffect(() => {
    if (mode !== "edit" || !passedContractor) return;

    setFormData((prev) => {
      const contractorMasId = pick(
        passedContractor.contractorMasId,
        passedContractor.fld_contractorid,
        passedContractor.id
      );

      const contractorId = pick(
        passedContractor.contractorId,
        passedContractor.contractorCode,
        passedContractor.fld_contractorcode
      );

      return {
        ...prev,
        contractorMasId: contractorMasId ? String(contractorMasId) : null,
        contractorId: String(contractorId || ""),

        contractorName: String(
          pick(
            passedContractor.contractorName,
            passedContractor.fld_contractorName
          ) || ""
        ),
        mobileNumber: String(
          pick(
            passedContractor.contactNumber,
            passedContractor.mobileNumber,
            passedContractor.mobileNo,
            passedContractor.fld_mobileno
          ) || ""
        ),

        branchId: String(
          pick(
            passedContractor.branchId,
            passedContractor.fld_branchid,
            prev.branchId
          ) || ""
        ),
        countryId: String(
          pick(
            passedContractor.countryId,
            passedContractor.fld_countryid,
            prev.countryId,
            "56"
          ) || "56"
        ),
        stateId: String(
          pick(
            passedContractor.stateId,
            passedContractor.fld_stateid,
            prev.stateId,
            "24"
          ) || "24"
        ),
        districtId: String(
          pick(
            passedContractor.districtId,
            passedContractor.fld_districtid,
            prev.districtId
          ) || ""
        ),

        landMark: String(
          pick(
            passedContractor.landMark,
            passedContractor.landmark,
            passedContractor.fld_landmark
          ) || ""
        ),
        address1: String(
          pick(
            passedContractor.address1,
            passedContractor.address,
            passedContractor.fld_address1
          ) || ""
        ),
        address2: String(
          pick(passedContractor.address2, passedContractor.fld_address2) || ""
        ),

        accNo: String(
          pick(passedContractor.accNo, passedContractor.fld_accnumber) || ""
        ),
        ifscCode: String(
          pick(passedContractor.ifscCode, passedContractor.fld_ifsccode) || ""
        ),
        bankName: String(
          pick(passedContractor.bankName, passedContractor.fld_bankname) || ""
        ),
        branchNameBank: String(
          pick(
            passedContractor.branchNameBank,
            passedContractor.fld_bankbranchname
          ) || ""
        ),
      };
    });
  }, [mode, passedContractor]);

  // ✅ Load Branch + Country
  useEffect(() => {
    if (!sessionBranchId && mode !== "edit") {
      toast.error("branchId not found. Please login again.");
      return;
    }

    const loadBaseDDLs = async () => {
      setLoadingDDL(true);
      try {
        const [brRes, cRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/contractors/branches`, {
            headers: commonHeaders,
          }),
          axios.get(`${BASE_URL}/api/contractors/countries`, {
            headers: commonHeaders,
          }),
        ]);

        setBranches(brRes?.data?.data || []);
        setCountries(cRes?.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dropdowns");
      } finally {
        setLoadingDDL(false);
      }
    };

    loadBaseDDLs();
  }, [sessionBranchId, mode, commonHeaders]);

  // ✅ Load States when country changes
  useEffect(() => {
    const countryid = formData.countryId;
    if (!countryid) return;

    const loadStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/contractors/states`, {
          params: { countryid },
          headers: commonHeaders,
        });
        setStates(res?.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load states");
      }
    };

    loadStates();
  }, [formData.countryId, commonHeaders]);

  // ✅ Load Districts when state changes
  useEffect(() => {
    const stateid = formData.stateId;
    if (!stateid) return;

    const loadDistricts = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/contractors/districts`, {
          params: { stateid },
          headers: commonHeaders,
        });
        setDistricts(res?.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load districts");
      }
    };

    loadDistricts();
  }, [formData.stateId, commonHeaders]);

  // ✅ Get branch name text from list
  const getBranchNameText = (branchId) => {
    const b = branches.find(
      (x) =>
        String(x.id ?? x.fld_branchid ?? x.branchid) === String(branchId)
    );
    return b?.name ?? b?.fld_branchname ?? b?.BranchName ?? "";
  };

  // ✅ Generate contractorId = BranchName/Code (ADD mode only)
  const fetchAndSetContractorCode = async (branchId) => {
    if (!branchId) {
      handleInputChange("contractorId", "");
      return;
    }

    if (mode === "edit") return;

    setLoadingCode(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/contractors/nextCode`, {
        params: { branchid: branchId },
        headers: commonHeaders,
      });

      const code = res?.data?.nextCode || "";
      if (!code) {
        toast.error("Code not received from server");
        return;
      }

      const branchName = getBranchNameText(branchId);
      if (!branchName) {
        toast.error("Branch name not found for selected branch");
        return;
      }

      // If backend already returns full format, keep as-is.
      // If backend returns only numeric part, you can switch to:
      // `${branchName}/${code}`
      handleInputChange("contractorId", `${code}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate contractor code");
    } finally {
      setLoadingCode(false);
    }
  };

  // ✅ On Country change reset state/district
  const onCountryChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      countryId: val,
      stateId: "",
      districtId: "",
    }));
    setStates([]);
    setDistricts([]);
  };

  const onStateChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      stateId: val,
      districtId: "",
    }));
    setDistricts([]);
  };

  // ✅ Validate
  const validate = () => {
    const required = [
      "contractorName",
      "mobileNumber",
      "branchId",
      "countryId",
      "stateId",
      "districtId",
      "address1",
      "accNo",
      "ifscCode",
      "bankName",
      "branchNameBank",
    ];

    const missing = required.filter((k) => !String(formData[k] || "").trim());
    if (missing.length) {
      toast.error("Please fill all required fields!");
      return false;
    }

    if (!/^\d{10}$/.test(String(formData.mobileNumber || ""))) {
      toast.error("Please enter a valid 10-digit mobile number!");
      return false;
    }

    if (
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(
        String(formData.ifscCode || "").toUpperCase()
      )
    ) {
      toast.error("Please enter a valid IFSC code!");
      return false;
    }

    if (mode === "add" && !String(formData.contractorId || "").trim()) {
      toast.error("Please select Branch to generate Contractor Id!");
      return false;
    }

    return true;
  };

  // ✅ Save (Insert/Update)
  const handleSubmit = async () => {
    if (!validate()) return;

    if (!userid || !sessionBranchId) {
      toast.error("userId/branchId missing. Please login again.");
      return;
    }

    const codeNumeric = extractCodeNumeric(formData.contractorId);

    if (mode === "add" && !codeNumeric) {
      toast.error("Invalid Contractor Id format. Please regenerate code.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        userid,
        branchid: sessionBranchId,
        contractorMasId: formData.contractorMasId || null,

        contractorId: formData.contractorId,
        codeNumeric: mode === "add" ? codeNumeric : null,

        contractorName: formData.contractorName,
        branchId: parseInt(formData.branchId, 10),

        countryId: parseInt(formData.countryId, 10),
        stateId: parseInt(formData.stateId, 10),
        districtId: parseInt(formData.districtId, 10),

        mobileNumber: formData.mobileNumber,
        landMark: formData.landMark,
        address1: formData.address1,
        address2: formData.address2,

        accNo: formData.accNo,
        ifscCode: String(formData.ifscCode || "").toUpperCase(),
        bankName: formData.bankName,
        branchNameBank: formData.branchNameBank,
      };

      console.log("payload:", payload);

      const res = await axios.post(
        `${BASE_URL}/api/contractors/save`,
        payload,
        {
          headers: commonHeaders,
        }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Saved successfully!");
        navigate(-1);
      } else {
        toast.error(res?.data?.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PaymentLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {mode === "edit" ? "Update Contractor" : "Contractor Entry"}
            </h1>
          </div>

          {/* Basic Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Contractor Id <span className="text-red-500">*</span>
              </label>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.contractorId}
                  readOnly
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-800 border-gray-600 text-gray-300"
                      : "bg-gray-100 border-gray-300 text-gray-700"
                  }`}
                  placeholder={mode === "add" ? "Select Branch to generate" : ""}
                />

                {mode === "add" && (
                  <button
                    type="button"
                    onClick={() => fetchAndSetContractorCode(formData.branchId)}
                    disabled={loadingCode || !formData.branchId}
                    className={`px-4 py-2 rounded-lg font-semibold ${buttonClass} ${hoverClass} text-white ${
                      loadingCode || !formData.branchId
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {loadingCode ? "..." : "Gen"}
                  </button>
                )}
              </div>

              <p
                className={`text-xs mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-500"
                }`}
              >
                {mode === "add"
                  ? "Contractor Id is generated using Branch Name + Next Code."
                  : "Contractor Id is readonly in edit mode."}
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Contractor Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contractorName}
                onChange={(e) =>
                  handleInputChange("contractorName", e.target.value)
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.mobileNumber}
                onChange={(e) =>
                  handleInputChange("mobileNumber", e.target.value)
                }
                maxLength="10"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Branch <span className="text-red-500">*</span>
              </label>

              <select
                value={formData.branchId}
                disabled={loadingDDL || mode === "edit"}
                onChange={async (e) => {
                  const newBranchId = e.target.value;
                  handleInputChange("branchId", newBranchId);
                  await fetchAndSetContractorCode(newBranchId);
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="">Select</option>
                {branches.map((b, idx) => {
                  const id = b.id ?? b.fld_branchid ?? b.branchid;
                  const name =
                    b.name ??
                    b.fld_branchname ??
                    b.BranchName ??
                    b.branchname;
                  return (
                    <option key={id ?? idx} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Address */}
          <div
            className={`mb-8 p-4 rounded-lg ${
              isDark ? "bg-gray-800/50" : "bg-blue-50"
            }`}
          >
            <h2
              className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-2 rounded`}
            >
              ADDRESS
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.countryId}
                  onChange={(e) => onCountryChange(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {countries.map((c, idx) => (
                    <option key={c.id ?? idx} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.stateId}
                  onChange={(e) => onStateChange(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {states.map((s, idx) => (
                    <option key={s.id ?? idx} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  District <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.districtId}
                  onChange={(e) =>
                    handleInputChange("districtId", e.target.value)
                  }
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {districts.map((d, idx) => (
                    <option key={d.id ?? idx} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Land Mark
                </label>
                <textarea
                  value={formData.landMark}
                  onChange={(e) => handleInputChange("landMark", e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg resize-none ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Address 1 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.address1}
                  onChange={(e) => handleInputChange("address1", e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg resize-none ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Address 2
                </label>
                <textarea
                  value={formData.address2}
                  onChange={(e) => handleInputChange("address2", e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg resize-none ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Bank */}
          <div
            className={`mb-8 p-4 rounded-lg ${
              isDark ? "bg-gray-800/50" : "bg-blue-50"
            }`}
          >
            <h2
              className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-2 rounded`}
            >
              BANK DETAILS
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Acc No <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.accNo}
                  onChange={(e) => handleInputChange("accNo", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  IFSC Code <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.ifscCode}
                  onChange={(e) =>
                    handleInputChange("ifscCode", e.target.value.toUpperCase())
                  }
                  maxLength="11"
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.bankName}
                  onChange={(e) => handleInputChange("bankName", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Branch Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.branchNameBank}
                  onChange={(e) =>
                    handleInputChange("branchNameBank", e.target.value)
                  }
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-center items-center gap-6">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold ${
                isDark
                  ? "bg-gray-600 hover:bg-gray-700 text-white"
                  : "bg-gray-500 hover:bg-gray-600 text-white"
              }`}
            >
              <FaArrowLeft />
              Back
            </button>

            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold ${buttonClass} ${hoverClass} text-white ${
                saving ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {saving ? "Saving..." : mode === "edit" ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorEntry;